'use client';

import { useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { FindingsListEnrichedQueryOptions } from '../../clients/tester-client/findings-list-enriched-query';
import { fetchFindingsListEnrichedPageForTarget } from './target-findings-api';
import { FINDINGS_QUERY_KEY } from './useFindings';

export const FINDINGS_LIST_PAGE_SIZE = 50;

export type FindingsListPagesOptions = Omit<
  FindingsListEnrichedQueryOptions,
  'skip' | 'limit'
>;

export const useFindingsListPages = ({
  targetId,
  targetType,
  options,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  options: FindingsListPagesOptions;
}) => {
  const query = useInfiniteQuery({
    queryKey: [FINDINGS_QUERY_KEY, 'list-pages', targetId, targetType, options],
    queryFn: ({ pageParam }) =>
      fetchFindingsListEnrichedPageForTarget({
        targetId: targetId ?? '',
        targetType,
        options: {
          ...options,
          skip: pageParam,
          limit: FINDINGS_LIST_PAGE_SIZE,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce(
        (sum, page) => sum + page.findings.length,
        0,
      );

      return lastPage.findings.length > 0 && loadedCount < lastPage.total
        ? loadedCount
        : undefined;
    },
    enabled: !!targetId,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === targetId
        ? keepPreviousData(previousData)
        : undefined,
  });
  const findings = useMemo(
    () => query.data?.pages.flatMap((page) => page.findings) ?? [],
    [query.data],
  );

  return {
    findings,
    totalCount: query.data?.pages[0]?.total ?? null,
    isInitialLoading: query.isPending,
    isRefetching: query.isRefetching && !query.isFetchingNextPage,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
